import { BOX_TYPE, CHART_TYPE, EXPORT_METHOD, GRAPH_INTERVAL, METRIC_AGGREGATE, MULTIPLIER, ORIENTATION, TABLE_STYLE_CLASS, TEXT_STYLE_CLASS, UNIT_PREFIX } from '@const/pageResponse.constants';
import { Status } from '@dto/healthStatus.dto';
import { AlertingDetectionCapabilityBox } from './alerting.dto';
import { Item } from './architecture.dto';
import { BasicResponseDTO } from './basicResponse.dto';
import { TOPOLOGY_TYPE } from './constants/pageResponse.constants';
import { ItemGroup } from './customGroup.dto';
import { DashboardGroup } from './dashboard.dto';
import { EstimatorBaseRequest, EstimatorData } from './estimator.dto';
import { HealthStatusMirrorDTO } from './healthStatusMirror.dto';
import { SnapshotDTO } from './snapshot.dto';
import { ProcessSample } from './processSnapshot.dto';
import { Unit } from './subsystemMetricsResponse.dto';
import { PredictionBase, TimeseriesCustom } from './timeseriesRequest.dto';

export type ChartType = typeof CHART_TYPE[keyof typeof CHART_TYPE];
export type BoxType = typeof BOX_TYPE[keyof typeof BOX_TYPE];
export type ExportMethod = typeof EXPORT_METHOD[keyof typeof EXPORT_METHOD];
export type GraphInterval = typeof GRAPH_INTERVAL[keyof typeof GRAPH_INTERVAL];
export type MetricAggregate = typeof METRIC_AGGREGATE[keyof typeof METRIC_AGGREGATE];
export type Multiplier = typeof MULTIPLIER[keyof typeof MULTIPLIER];
export type TableStyleClass = typeof TABLE_STYLE_CLASS[keyof typeof TABLE_STYLE_CLASS];
export type TextStyleClass = typeof TEXT_STYLE_CLASS[keyof typeof TEXT_STYLE_CLASS];
export type UnitPrefix = typeof UNIT_PREFIX[keyof typeof UNIT_PREFIX];
export type TopologyType = typeof TOPOLOGY_TYPE[keyof typeof TOPOLOGY_TYPE];
export type OrientationType = typeof ORIENTATION[keyof typeof ORIENTATION];

export class IWithUnit {
  unit: Unit;
  shortcut: string;
  defaultPrefix?: UnitPrefix;
  unitLabel?: string;
}

export class IMetricType extends IWithUnit {
  defaultPrecision?: number;
  metricTypeLabel?: string;
  metricType?: string;
  /**
   * Lowest TOP Y axis range, takes maximum of all metrics. Higher timeseries values will push the range up.
   * Shared among all FourGraphBox graphs
   */
  minY?: number[];
  /**
   * If true, use max of timeseries values as TOP Y axis range even for % unit.
   */
  maxY?: boolean;
  stackable?: boolean;
}

export class Metric extends IMetricType {
  type: ChartType;
  stacked: boolean;
  metric: string;
  // negative order value to display below X axis
  order: number;
  aggregate?: MetricAggregate;
  unit: Unit;
  unitLabel?: string;
  shortcut: string;
  label?: string;
  /** Standalone name for contexts without a graph title (e.g. dashboard legend) */
  longLabel?: string;
  color?: string;
  forceColor?: boolean;
  defaultPrefix?: UnitPrefix;
  defaultPrecision?: number;
  minY?: number[];
  maxY?: boolean;
  /**
   * Place metric in table above line separating items below
   */
  legendTop?: boolean;
  /**
   * Visible in table legend, not plotted
   */
  legendOnly?: boolean;
  /**
   * Show thermometer bar in table (like CapacityTableBox)
   */
  thermometer?: boolean;
}

//export class TableColumn {
//  order: number;
//  property?: string;
//  metric?: string;
//  relation?: string;
//}


export class CapacityTableBase {
  header: CapacityTableHeader[];
  rows: CapacityTableColumn[][];
  summaryRow?: CapacityTableColumn[];
  export?: Export;
  id: string;
  disableImport?: boolean;
}

export class TableHeaderBase {
  order: number;
  value: string;
  wrappedValue?: string;
  prefix?: UnitPrefix;
  number?: boolean;
  decimal?: number;
  shortcut?: string;
}

export class CapacityTableHeader extends TableHeaderBase {
  thermometer?: boolean;
  parentHeader?: string;
}

export class CapacityTableColumn {
  order: number;
  value: number | string;
  url?: string;
  tooltip?: string;
}

export class CapacityTableBox extends CapacityTableBase implements Box {
  type = BOX_TYPE.CapacityTableBox;
}

export class TableHeader extends TableHeaderBase {
  unit?: Unit;
  multiplier?: 1000 | 1024;
  styleClass?: TableStyleClass;
  sort?: 'desc' | 'asc';
  noWrap?: boolean;
  trim: boolean;
  group?: string; // deprecated
  newLines?: boolean;
  linuxMpath?: boolean;
  trimLength?: number;
  disable?: {
    search?: boolean;
    sort?: boolean;
  };
  tags?: boolean;
  invertColors?: boolean;
}

export class TableGroupHeader {
  order: number;
  value: string;
  wrappedValue?: string;
  group?: string;
  number?: boolean;
  unit?: Unit;
  shortcut?: string;
  prefix?: UnitPrefix;
  decimal?: number;
  defaultPrefix?: UnitPrefix;
  multiplier?: 1000 | 1024;
  styleClass?: TableStyleClass;
  sort?: 'desc' | 'asc';
  noWrap?: boolean;
  trim: boolean;
  newLines?: boolean;
  linuxMpath?: boolean;
  trimLength?: number;
  disable?: {
    search?: boolean;
    sort?: boolean;
  };
  tags?: boolean;
}

export class TableGroup {
  group: string;
  id?: string;
  unit?: Unit;
  shortcut?: string;
  prefix?: UnitPrefix;
  number?: boolean;
  decimal?: number;
  styleClass?: TableStyleClass;
  multiplier?: 1000 | 1024;
  trim: boolean;
}

export class TableCell {
  order: number;
  value: (string | number)[];
  styleClass?: TableStyleClass;
  bgStatus?: Status;
  bgColor?: string;
  url?: string[];
  tooltip?: string;
  modal?: Box[];
  noTrim?: boolean;
  number?: boolean;
}

export interface Box {
  type: BoxType;
  help?: Help;
}

export class TextStyled {
  text: string;
  styleClass?: TextStyleClass;
}

export class TextBox extends TextStyled implements Box {
  type = BOX_TYPE.TextBox;
}

export class HtmlTextBox implements Box {
  type = BOX_TYPE.HtmlTextBox;
  html: string;
}

export class HealthCheckBox implements Box {
  type = BOX_TYPE.HealthCheckBox;
}

export class DashboardBox implements Box {
  type = BOX_TYPE.DashboardBox;
  groups: DashboardGroup[];
  width: number;
}

export class SunburstItem {
  item_id: string;
  label: string;
  parent_id: string;
  parent_label: string;
  value: number;
}

export class SunburstBox implements Box {
  type = BOX_TYPE.SunburstBox;
  items: SunburstItem[];
}

export class StaticMetric {
  type: ChartType;
  label: string;
  value: number;
  unit: Unit;
  shortcut: string;
}

export class GraphTableSort {
  metric: string;
  order: 'avg' | 'max';
}

export interface GraphThreshold {
  value: number;
  label?: string;
  color?: string;
}

export interface GraphTriggerPeriod {
  start: number; // unix seconds
  end: number; // unix seconds
  color?: string;
}

export class GraphBase {
  aggregated?: boolean;
  label?: string;
  graphLabel?: string;
  parent?: GraphBaseParent;
  metrics: Metric[];
  minInterval?: number;
  peakMetric?: Metric;

  // custom properties in graph table
  properties?: GraphTableProperty[];

  // table sorting
  sort?: GraphTableSort;

  // skip items with null values
  skipNull?: boolean;

  staticMetrics?: StaticMetric[];
  uuids: string[];

  // override interval for timeseries data requests (in minutes)
  overrideInterval?: number;

  withoutDeleted?: boolean;

  // fixed horizontal reference lines (e.g. alert thresholds)
  thresholds?: GraphThreshold[];

  // shaded time ranges (e.g. periods an alert was triggered)
  triggerPeriods?: GraphTriggerPeriod[];
  // for summary purposes, disables automatic color synchronization across graphs
  noSyncColors?: boolean;

  // custom timeseries source, sent as TimeseriesRequestDTO.custom (uuids stay empty)
  custom?: TimeseriesCustom;
}

export class EstimatorMultipliedMetric {
  item_id: string;
  label: string;
  parentLabel?: string;
  metric: Metric;
  timeseries: object | number[]; // keep here??
  avg: number;
  max: number;
  smt: number;
  fix: string;
}

export class EstimatorGraphGroup {
  headerLabel: string; // e.g. will be migrated
  metrics: EstimatorMultipliedMetric[];
}

/**
 * compare: GraphTableProperty
 * graph line + extra legend
 * color is black
 */
export class ExtraGraphProperty {
  label: string;
  value: number; // to graph
  smt: number;
}

export class EstimatorGraph {
  targetMetricName: "rPerf" | "CPW" | "CPU";
  parent: GraphBaseParent; // always server
  sourceLoad: EstimatorGraphGroup;
  targetLoad?: EstimatorGraphGroup;
  extraProps: ExtraGraphProperty[]; // vs GraphTableProperty
  dateRange: {
    start: number,
    end: number,
  };
}

export class EstimatorGraphBox implements Box {
  type = BOX_TYPE.EstimatorGraphBox;
  graph: EstimatorGraph;
}

export class GraphBaseParent {
  label: string;
  subsystem: string;
}

export class GraphTableProperty {
  label: string;
  search?: boolean;
  values: Record<string, GraphTablePropertyItem>;
  showInGraph?: boolean;
  unit?: Unit;
  shortcut?: string;
  defaultPrefix?: UnitPrefix;
  decimal?: number;
}

export class GraphTablePropertyItem {
  value: string;
  url?: string;
}

export class SyncGraphBox implements Box {
  type = BOX_TYPE.SyncGraphBox;
  graphs: GraphBase[];
}

export class MultiGraphBox<T extends GraphBase = GraphBase> implements Box {
  type = BOX_TYPE.MultiGraphBox;
  graphs: T[];
  noFlex?: boolean;
  start?: number; // has priority over start in graphs
  end?: number; // has priority over end in graphs
  sync?: boolean; // undefined = false
}

export class GraphBaseInterval extends GraphBase {
  interval: GraphInterval;
}

export class GraphBaseTimeRange extends GraphBase {
  start: number; // in secs
  end: number; // in secs
}

export class SingleTimeRangeGraphBox extends GraphBaseTimeRange implements Box {
  type = BOX_TYPE.SingleTimeRangeGraphBox;
}

export class SingleGraphBox extends GraphBaseInterval implements Box {
  type = BOX_TYPE.SingleGraphBox;
}

export class SummaryBoxConfigurationSectionData {
  label: string; // conf label
  value: (string | number)[]; // conf value
  inline?: boolean; // show key & value in one row
  url?: string[];

  decimal?: number;
  unit?: Unit;
  shortcut?: string;
  prefix?: UnitPrefix;

  bgStatus?: Status; // for HS
}

export class SummaryBoxConfigurationSection {
  section: string; // section label
  data: SummaryBoxConfigurationSectionData[];
  collapse?: boolean;
}

export class SummaryBox implements Box {
  type = BOX_TYPE.SummaryBox;
  sections: SummaryBoxConfigurationSection[];
  content: Box[]; // graphs & tables
  template?: boolean;
  item_id: string;

  static isSummaryBox(box: Box): box is SummaryBox {
    return box.type === BOX_TYPE.SummaryBox;
  }
}

export class PredictionMetadata {
  label: string;
  key: string;
  number: boolean;
}

export class PredictionGraphBox extends PredictionBase implements Box {
  type = BOX_TYPE.PredictionGraphBox;
  graphLabel: string;
  metric: Metric;
  other_metrics: Metric[];
  metadata: PredictionMetadata[];
  lowerMax?: number;
  help?: Help;
}

export class Help {
  text: string;
  url: string;
}

export class DriveGanttLegendStatus {
  color: string;
  label: string;
  text: string;
}

export class DriveGanttLegend {
  type: 'driveOperationalStatus' | 'jobOperationalStatus';
  label: string;
  text?: string;
  statuses: DriveGanttLegendStatus[];
}

export class FourGraphBox extends GraphBase implements Box {
  type: BoxType = BOX_TYPE.FourGraphBox;
}

export class PerformanceColumn {
  // metric key — must match a key in the parent PerformanceBox metrics[]
  metric: string;
  label?: string;
  // show thermometer bar (like CapacityTableBox)
  thermometer?: boolean;
  unit?: Unit;
  shortcut?: string;
  defaultPrefix?: UnitPrefix;
  decimal?: number;
  // default true
  sortable?: boolean;
}

export class PerformanceBox extends GraphBase implements Box {
  type: BoxType = BOX_TYPE.PerformanceBox;
  // metric columns to display in the item table
  columns?: PerformanceColumn[];
  // which property labels (from properties[]) get sidebar filter panels
  filterProperties?: string[];
  // selectable time ranges; defaults to all four when omitted
  timeRanges?: GraphInterval[];
  // override the default 'Name' column header in the item table (e.g. 'PID')
  nameColumnLabel?: string;
  // item uuid -> URL for the Name column link
  nameUrls?: Record<string, string>;
  // plural noun of the listed items (e.g. 'VMs'); when set, a footer tells how many items are displayed under the top-items limit
  itemLabel?: string;
  // tab of the page this box belongs to; sent back with the filter request when the page holds several PerformanceBoxes
  tab?: string;
}

export class PerformanceFilterRequestDTO {
  hw_type: string;
  type: string;
  item_id?: string;
  group_id?: string;
  tab?: string;
  selectedFilters: Record<string, string[]>;   // sidebar: {propertyLabel: [selected values]}
  columnFilters?: Record<string, string[]>;     // table header filters: {columnLabel: [selected values]} — property OR metric columns
  start: number;
  end: number;
  top?: number;
}

export class PerformanceFacetValue {
  value: string;
  count: number;
}

export class PerformanceFacet {
  label: string;
  values: PerformanceFacetValue[];
}

export class PerformanceFilterResponseDTO {
  facets: PerformanceFacet[];   // one per filterProperties label (sidebar) AND per property/metric column (table header)
  uuids: string[];              // final narrowed uuid list, capped at `top` if provided
  totalMatched: number;         // count before the `top` cap, for "showing N of M" UI
}

export class PerformanceFilterResponseWrapperDTO extends BasicResponseDTO {
  data: PerformanceFilterResponseDTO;
}

export class RegroupGraph extends GraphBase {
  url: string;
  tab: string;
}

export class RegroupButtonBox implements Box {
  type = BOX_TYPE.RegroupButtonBox;
  graphs: RegroupGraph[];
}


export class TableBase {
  header: TableHeader[];
  rows: TableCell[][];
  fixedCols?: number;
  export?: Export;
  headerNoWrap?: boolean;
  orientation: OrientationType;
  headerNoUnit?: boolean;
  reload?: number; // in minutes
  stretch?: boolean;
  label?: string; // table label
  visibleRows?: number;
}

export interface StartEnd {
  start: number;
  end: number;
  timezone?: string; // User timezone (IANA format, e.g., 'Europe/Prague', 'America/Chicago')
}

export class TableContentRequest<T extends StartEnd = Record<string, any> & StartEnd> {
  url: string;
  body?: T; // maybe add generic in future
}


export class TableBox extends TableBase implements Box {
  type = BOX_TYPE.TableBox;
  contentRequest?: TableContentRequest;
}

export class HsTableBox extends TableBase implements Box {
  type = BOX_TYPE.HsTableBox;
  contentRequest?: TableContentRequest;
  legendBox?: LegendBox;
  stateTimelineBox?: StateTimelineBox<string | number>;
}

export class MirrorBox implements Box {
  type = BOX_TYPE.MirrorBox;
  mirrors?: HealthStatusMirrorDTO[];
}

export class SnapshotBox implements Box {
  type = BOX_TYPE.SnapshotBox;
  snapshots?: SnapshotDTO[];
}

export class ProcessSnapshotBox implements Box {
  type = BOX_TYPE.ProcessSnapshotBox;
  item_id: string;
  latestTimestamp: number;
}

export class TableGroupBox implements Box {
  type = BOX_TYPE.TableGroupBox;
  tables: TableBox[] | TableHeaderGroupBox[];
}

export interface SavingsConfig {
  cpuColumnIndex: number;
  memoryColumnIndex: number;
  cpuCoresSaved: number;
  cpuCoresRequired: number;
  cpuNetChange: number;
  memoryBytesSaved: number;
  memoryBytesRequired: number;
  memoryNetChange: number;
}

export class TableHeaderGroupBox implements Box {
  type = BOX_TYPE.TableHeaderGroupBox;
  header: TableGroupHeader[];
  rows: TableCell[][];
  groups: TableGroup[];
  orientation: OrientationType;
  contentRequest?: TableContentRequest;
  stretch?: boolean;
  label?: string;
  visibleRows?: number;
  fixedCols?: number;
  reload?: number;
  horizontalVirtualization?: boolean;
  maxVisibleColumns?: number;
  help?: Help;
  savingsConfig?: SavingsConfig;
}

export class TablePivotBox extends TableBase implements Box {
  type = BOX_TYPE.TablePivotBox;
}

export interface ExportBody {
  headers?: unknown[];
  values?: unknown[];
}

export class Export<TBody = object> {
  fileName?: string;
  url: string;
  method: ExportMethod;
  type?: "static" | "update_time";
  body?: TBody;
}

export class TopBoxTable extends TableBase {
  label: string;
  export: Export;
}

export class TopBox implements Box {
  type: BoxType = BOX_TYPE.TableBox;
  tables: TopBoxTable[];
}

export class PortHealthStatusPort {
  label: string;
  url: string;
  operationalStatus: string;
  operationalStatusColor: string;
  physicalStatus?: string;
  physicalStatusColor?: string;
  adminStatus?: string;
  adminStatusColor?: string;
  operationalStatusReason?: string;
  operationalStatusReasonColor?: string;
}

export class PortHealthStatusDevice {
  label: string;
  url?: string;
  ports: PortHealthStatusPort[];
}

export class PortHealthStatusLegendStatus {
  color: string;
  label: string;
  text?: string;
}

export const PORT_STATUS_TYPE = ['operationalStatus', 'physicalStatus', 'adminStatus', 'operationalStatusReason'] as const;
export type PortStatusType = typeof PORT_STATUS_TYPE[number];

export class PortHealthStatusLegend {
  type: PortStatusType;
  label: string;
  text?: string;
  statuses: PortHealthStatusLegendStatus[];
}

export class PortHealthStatusHwType {
  label: string;
  devices: PortHealthStatusDevice[];
  legends: PortHealthStatusLegend[];
}

export class PortHealthStatusBox implements Box {
  type = BOX_TYPE.PortHealthStatusBox;
  hwTypes: PortHealthStatusHwType[];
  tabs?: Tab[];
}

export interface IHeatmap {
  label: string;
  metric: string;
  items: {
    item_id?: string;
    label: string;
    percent: number;
    url: string;
  }[];
}
export class HeatmapBox implements Box {
  type = BOX_TYPE.HeatmapBox;
  metrics: IHeatmap[];
}

export class OverviewRequest {
  item_id: string;
  start: number; // in secs
  end: number; // in secs
}

export class OverviewItem {
  label: string;
  item_id: string;
}

export class OverviewCategory {
  label: string;
  items: OverviewItem[];
}

export class OverviewBox implements Box {
  type = BOX_TYPE.OverviewBox;
  categories: OverviewCategory[];
}

// NOTE: request is in estimator.dto

export class EstimatorFormBox implements Box {
  type = BOX_TYPE.EstimatorBox;
  data: EstimatorData;
}

export class EstimatorRequest extends EstimatorBaseRequest { }

export class EstimatorResultBox implements Box {
  // TBD
  type = BOX_TYPE.EstimatorBox;
  selection: EstimatorData;
}

export class CustomTopBox {
  type = BOX_TYPE.CustomTopBox;
  hw_type: string;
  subsystem: string;
  metrics: string[];
  parentsSubsystem?: string[];
  pageLabel?: string;
  showItemGroupSelect?: boolean;
  showMetricsSelect?: boolean;
}

export class CustomTopRequest {
  hw_type: string;
  subsystem: string;
  type: 'avg' | 'max';
  metrics: string[];
  start: number; // in secs
  end: number; // in secs
  parentsSubsystem?: string[];
  pageLabel?: string;
  itemGroup?: ItemGroup;
}

export class LogRequestBox {
  type = BOX_TYPE.LogRequestBox;
  item_ids: string[];
  show: {
    item: boolean;
    parent: boolean;
    severity: boolean;
  };
  source: 'aix' | 'linux' | 'solaris' | 'custom';
  text?: string;
  metadata?: Record<string, string>;
  interval?: 'day' | 'week' | 'month' | 'year';
  lastSeconds?: number; // alternative to interval, e.g. lastSeconds: 3600 for last hour
  available?: boolean;
}

export class EventRequestBox {
  type = BOX_TYPE.EventRequestBox;
  item_ids?: string[];
  event_type: string;
  columns: string[];
}

export class StorageHeatmapInterval {
  color: string;
  limit: number;
}

export class StorageHeatmapItem {
  device: string;
  label: string;
  item_id: string;
  read?: StorageHeatmapItemMetric;
  write?: StorageHeatmapItemMetric;
  total?: StorageHeatmapItemMetric;
  url: string;
  sortValue?: number;
}


export class StorageHeatmapItemMetric {
  line: string;
  color: string;
  value?: number;
}


export class StorageHeatmapTier {
  items: StorageHeatmapItem[];
  read_intervals: StorageHeatmapInterval[];
  write_intervals: StorageHeatmapInterval[];
}

export class StorageHeatmapTiers {
  [tier: string]: StorageHeatmapTier;
}

export class StorageHeatmapBox implements Box {
  type = BOX_TYPE.StorageHeatmapBox;
  tiers: StorageHeatmapTiers;
}

export class TopologyConnectionWwn {
  sourceWwn: string;
  destinationWwn: string;
  sourceType: string;
  destinationType: string;
  percent?: number;
  text: string;
  isWwn: boolean;
  hs?: Status;
}

export class TopologyConnection {
  sourceId: string;
  destinationId: string;
  sourceType: string;
  destinationType: string;
  percent?: number;
  text: string;
  isWwn: boolean;
  hs?: Status;
}

export class TopologyItem {
  label: string;
  id: string;
  wwn?: string;
  url?: string;
  childrenLabel?: string;
  children?: TopologyItem[];
  groups?: TopologyGroup[];
}

export class TopologyGroup {
  label: string;
  type: string;
  children: TopologyItem[];
}

export class TopologyRequest {
  item_id?: string;
  type: TopologyType;
  hw_type?: string;
  level?: string;
}

export class TopologyRequestItem extends TopologyRequest {
  label: string; // tab label
}

export class TopologyRequestBox implements Box {
  type = BOX_TYPE.TopologyRequestBox;
  topologies: TopologyRequestItem[];
  help?: Help;
}

export class ConfigurationChangesBox implements Box {
  type = BOX_TYPE.ConfigurationChangesBox;
  hw_type?: string;
  subsystem?: string;
  item_ids?: string[];
}

export type Layer = string | { type: string, nest: Layer[]; };

export interface Layout {
  label: string;
  default?: boolean;
  layout: Layer[];
}

export class TopologyBox implements Box {
  type = BOX_TYPE.TopologyBox;
  schema: 'default' | 'template';
  layouts?: Layout[];
  connections?: TopologyConnection[];
  groups: TopologyGroup[];
  limit: number; // limit for showing without warning
}

export class Tab<T extends Box = Box> {
  title: string;
  content?: T[];
  lazyContent?: string; // URL to get content
  export?: Export;
  help?: Help;
  tabId?: string; // unique id for tab, optional
  subTabs?: SubTab[];
  subsystem?: string;
}

export type SubTab = Omit<Tab, "subTabs">;

export interface MenuFormPageItem { hwType: string, subsystem: string, class: string; };
export class MenuFormPage {
  hwTypes: MenuFormPageItem[];
  title: string;
}

export class Page {
  title: string;
  tabs: Tab[];
  formPage?: MenuFormPage;
  // Set when a page's content is sourced from another page type via a non-standard route
  // (e.g. the Self Monitoring settings page reusing LinuxServerPage.getPage()), so
  // PerformanceBox sidebar filtering can resolve the right menu context even though the
  // page's own fetch URL doesn't follow the /page/{hw_type}/{type} pattern.
  menuContext?: { hw_type: string; type: string; item_id?: string; group_id?: string; };

  constructor(title: string, tabs: Tab[], formPage?: MenuFormPage) {
    this.title = title;
    this.tabs = tabs;
    this.formPage = formPage;
  }
}

export class PageResponseDTO extends BasicResponseDTO {
  data: Page;
}

export class TabResponseDTO extends BasicResponseDTO {
  data: Tab;
}

export class EstimatorResponseDTO extends BasicResponseDTO {
  data: Tab;
}

export class TrendsResponseDTO extends BasicResponseDTO {
  data: Page;
}

export class SummaryResponseDTO extends BasicResponseDTO {
  data: Tab;
}

export class OverviewResponseDTO extends BasicResponseDTO {
  data: Tab;
}

export class CustomTopResponseDTO extends BasicResponseDTO {
  data: Tab;
}

export class HeatmapContent {
  heatmapBox: HeatmapBox | StorageHeatmapBox;
  textBox: TextBox;
  textBox2: TextBox;
}

export class TableRowsResponseDTO extends BasicResponseDTO {
  data: TableCell[][];
}

export class TableBoxResponseDTO extends BasicResponseDTO {
  data: TableBox;
}

export class HistoricalReportBox implements Box {
  type = BOX_TYPE.HistoricalReportBox;
  items: Item[];
}

export interface LegendBoxItems { label: string, color: string; };
export interface LegendBoxSections { label: string, items: LegendBoxItems[]; }
export class LegendBox implements Box {
  type = BOX_TYPE.LegendBox;
  sections: LegendBoxSections[];
  description?: HtmlTextBox;
}

export interface AlertingDetectionCapabilityDTO extends BasicResponseDTO {
  data: AlertingDetectionCapabilityBox;
}

// State Timeline types
export interface StateTimelineItemSpan<TState extends string | number> {
  start: number;
  end: number;
  state: TState;
  tooltipValue?: string; // Additional value for tooltip display (e.g., Cartridge name, Device name)
}

export interface StateTimelineItem<TState extends string | number> {
  id: string;
  start: number;
  end: number;
  state: TState;
  tooltipValue?: string; // Additional value for tooltip display (e.g., Cartridge name, Device name)
}

export type StateTimelineData<TState extends string | number> = Record<string, StateTimelineItem<TState>[]>;

export interface StateTimelineStateInfo {
  color: string;
  label: string;
  text?: string;
  tooltip?: string; // Use # as placeholder for tooltipValue (e.g., "Cartridge # is ready")
}

export interface StateTimelineStateConfig<TState extends string | number> {
  states: readonly TState[];
  legend: {
    title: string;
    description: string;
  };
  info: Record<TState, StateTimelineStateInfo>;
  // When set, the legend renders states grouped under these titled sections (with a divider
  // between them) instead of a single flat list. Falls back to `states` when omitted.
  legendSections?: { title: string; states: TState[]; }[];
}

export interface StateTimelineMetadata<TState extends string | number> {
  stateConfig: StateTimelineStateConfig<TState>;
  itemLabels: Record<string, string>;
  itemUrls?: Record<string, string>; // URL for each item/row (e.g., device URL)
  deviceType?: string;
  primaryRowIds?: string[]; // Rows to visually emphasize (bold label + divider), e.g. a group row above per-metric rows
}

export enum InitialZoom {
  Full = 0,
  LastHour = 1,
  Last4Hours = 4,
  Last6Hours = 6,
  Last12Hours = 12,
  LastDay = 24,
  LastTwoDays = 48
}

export interface StateTimelineOptions {
  initialZoom?: InitialZoom;
  maxHeight?: number;
  showCursor?: boolean;
  refreshIntervalMs?: number;
}

export class StateTimelineBox<TState extends string | number = string | number> implements Box {
  type = BOX_TYPE.StateTimelineBox;
  label?: string;
  data: StateTimelineData<TState>;
  metadata: StateTimelineMetadata<TState>;
  range: { start: number; end: number; };
  options?: StateTimelineOptions;
  help?: Help;
  contentRequest?: TableContentRequest; // API URL for refresh/date change requests
}

export class StateTimelineResponseDTO<TState extends string | number = string> extends BasicResponseDTO {
  data: StateTimelineBox<TState>;
}

export class SingleTimeRangeGraphResponseDTO extends BasicResponseDTO {
  data: SingleTimeRangeGraphBox;
}